// src/frontend/src/api/api.ts - Fixed backend URL configuration
import axios from 'axios';

// 🔧 FIXED: Determine the correct backend URL based on environment
const getBaseURL = () => {
  // In development, explicitly use the backend port
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5050/api';
  }
  
  // In production, use environment variable
  if (process.env.REACT_APP_API_URL) {
    const baseUrl = process.env.REACT_APP_API_URL;
    // Check if /api is already included to avoid duplication
    if (baseUrl.endsWith('/api')) {
      return baseUrl;
    } else {
      return `${baseUrl}/api`;
    }
  }

  
  // Fallback to relative path for production
  return '/api';
};

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    // Log error for debugging
    console.error('API Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
    });
    
    return Promise.reject(error);
  }
);

// 🔧 FIXED: Correct auth endpoints to match backend routes
export const authService = {
  login: (credentials: any) => api.post('/users/login', credentials), // FIXED: was /auth/login
  register: (userData: any) => api.post('/users', userData), // FIXED: was /auth/register  
  logout: () => api.post('/users/logout'), // FIXED: was /auth/logout
  refreshToken: () => api.post('/users/refresh'), // FIXED: was /auth/refresh
  googleAuth: (token: string) => api.post('/users/google-auth', { token }), // FIXED: was /auth/google
  verifyToken: () => api.get('/users/verify'), // FIXED: was /auth/verify
  forgotPassword: (email: string) => api.post('/users/forgot-password', { email }),
  resetPassword: (token: string, password: string) => 
    api.post('/users/reset-password', { token, password }),
};

// User profile and account services
export const userService = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data: any) => api.put('/users/profile', data),
  deleteAccount: () => api.delete('/users/profile'),
  changePassword: (oldPassword: string, newPassword: string) => 
    api.put('/users/change-password', { oldPassword, newPassword }),
  getUserStats: () => api.get('/users/stats'),
  getAccountSettings: () => api.get('/users/settings'),
  updateAccountSettings: (settings: any) => api.put('/users/settings', settings),
};

// Payment and subscription services
export const paymentService = {
  getPaymentMethods: () => api.get('/payments/methods'),
  addPaymentMethod: (data: any) => api.post('/payments/methods', data),
  updatePaymentMethod: (id: string, data: any) => api.put(`/payments/methods/${id}`, data),
  deletePaymentMethod: (id: string) => api.delete(`/payments/methods/${id}`),
  
  // Subscription management
  getSubscription: () => api.get('/subscriptions/current'),
  createSubscription: (data: any) => api.post('/subscriptions', data),
  updateSubscription: (data: any) => api.put('/subscriptions/current', data),
  cancelSubscription: () => api.post('/subscriptions/cancel'),
  resumeSubscription: () => api.post('/subscriptions/resume'),
  
  // Billing
  getBillingHistory: () => api.get('/payments/billing-history'),
  getInvoice: (invoiceId: string) => api.get(`/payments/invoices/${invoiceId}`),
  downloadInvoice: (invoiceId: string) => 
    api.get(`/payments/invoices/${invoiceId}/download`, { responseType: 'blob' }),
};

// Team management services (for team plans)
export const teamService = {
  getTeam: () => api.get('/teams/current'),
  createTeam: (data: any) => api.post('/teams', data),
  updateTeam: (data: any) => api.put('/teams/current', data),
  deleteTeam: () => api.delete('/teams/current'),
  
  // Team members
  getTeamMembers: () => api.get('/teams/members'),
  inviteMember: (data: any) => api.post('/teams/members/invite', data),
  removeMember: (memberId: string) => api.delete(`/teams/members/${memberId}`),
  updateMemberRole: (memberId: string, role: string) => 
    api.put(`/teams/members/${memberId}/role`, { role }),
  
  // Team analytics
  getTeamAnalytics: () => api.get('/teams/analytics'),
  getTeamProgress: () => api.get('/teams/progress'),
  getTeamUsage: () => api.get('/teams/usage'),
  exportTeamData: () => api.get('/teams/export', { responseType: 'blob' }),
};

// Learning tracks and progress services
export const learningService = {
  getLearningTracks: () => api.get('/learning-tracks'),
  getLearningTrackById: (id: string) => api.get(`/learning-tracks/${id}`),
  startLearningTrack: (id: string) => api.post(`/learning-tracks/${id}/start`),
  completeLearningTrack: (id: string) => api.post(`/learning-tracks/${id}/complete`),
  
  // Progress tracking
  getUserProgress: () => api.get('/learning-tracks/progress'),
  updateProgress: (trackId: string, scenarioId: string, data: any) => 
    api.put(`/learning-tracks/${trackId}/scenarios/${scenarioId}/progress`, data),
  
  // Achievements and badges
  getAchievements: () => api.get('/achievements'),
  unlockAchievement: (achievementId: string) => api.post(`/achievements/${achievementId}/unlock`),
};

// Analytics and reporting services
export const analyticsService = {
  getUserAnalytics: () => api.get('/analytics/user'),
  getPerformanceMetrics: () => api.get('/analytics/performance'),
  getUsageStats: () => api.get('/analytics/usage'),
  getSkillAssessment: () => api.get('/analytics/skills'),
  
  // Export reports
  exportAnalytics: (format: 'pdf' | 'csv') => 
    api.get(`/analytics/export?format=${format}`, { responseType: 'blob' }),
  generateReport: (type: string, options: any) => 
    api.post('/analytics/reports', { type, options }),
};

// Generic API utility functions
export const apiUtils = {
  // File upload utility
  uploadFile: (file: File, endpoint: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Health check
  healthCheck: () => api.get('/health'),
  
  // Generic GET with query params
  getWithParams: (endpoint: string, params: any) => 
    api.get(endpoint, { params }),
};

export default api;